/**
 * Kraken timeframe configuration and interval mapping.
 * Intervals are in minutes: 1, 5, 15, 30, 60, 240, 1440, 10080, 21600.
 * @see https://docs.kraken.com/rest/#tag/Market-Data/operation/getOHLCData
 * @see https://docs.kraken.com/api/docs/websocket-v2/ohlc
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * Kraken supported kline intervals.
 */
export const KRAKEN_TIMEFRAMES: TimeframeConfig = {
  native: ['1', '5', '15', '30', '60', '240', '1440', '10080', '21600'],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('kraken', KRAKEN_TIMEFRAMES);

/**
 * Maps TradingView resolution to Kraken interval format (in minutes).
 */
export const mapToKrakenInterval = (resolution: string): number => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals (return minutes)
  if (seconds === 60) return 1;
  if (seconds === 300) return 5;
  if (seconds === 900) return 15;
  if (seconds === 1800) return 30;
  if (seconds === 3600) return 60;
  if (seconds === 14400) return 240; // 4 hours
  if (seconds === 86400) return 1440; // 1 day
  if (seconds === 604800) return 10080; // 1 week
  if (seconds === 1814400) return 21600; // ~21 days (month-ish)

  // Default to closest supported interval (round down to nearest supported)
  if (seconds < 60) return 1;
  if (seconds < 300) return 1;
  if (seconds < 900) return 5;
  if (seconds < 1800) return 15;
  if (seconds < 3600) return 30;
  if (seconds < 14400) return 60;
  if (seconds < 86400) return 240;
  if (seconds < 604800) return 1440;
  if (seconds < 1814400) return 10080;

  return 21600;
};
